import apiClient from './client';

export type SemesterStatus = 'draft' | 'published' | 'archived';
export type AttendanceStatus = 'present' | 'late' | 'excused' | 'absent';
export type TrainerSlotName = 'lead' | 'co';

export interface TrainingVenue {
  id: number;
  name: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  notes: string | null;
  isActive: boolean;
  address: string;
  mapUrl: string | null;
}

export interface TrainingOutcome {
  id?: number;
  text: string;
  isDraft: boolean;
}

export interface TrainerSlot {
  slot: TrainerSlotName;
  claimedAt: string | null;
  trainer: { id: number; name: string; email?: string } | null;
}

export interface TrainingSession {
  id: number;
  semesterId: number;
  sortOrder: number;
  module: string;
  part: number | null;
  title: string;
  displayTitle: string;
  description: string | null;
  startsAt: string;
  endsAt: string;
  date: string;
  startTime: string;
  durationMinutes: number;
  durationOverride: number | null;
  timezone: string;
  venue: TrainingVenue | null;
  notes: string | null;
  status: 'scheduled' | 'cancelled';
  outcomes: TrainingOutcome[];
  trainers: TrainerSlot[];
}

export interface TrainingSemester {
  id: number;
  name: string;
  status: SemesterStatus;
  trainerManualUrl: string | null;
  planterManualUrl: string | null;
  defaultStartTime: string;
  defaultDurationMinutes: number;
  timezone: string;
  completionRequiredSessions: number | null;
}

export interface TrainingSemesterSummary extends TrainingSemester {
  sessionCount: number;
  firstSessionAt: string | null;
  lastSessionAt: string | null;
  trainerSlotsFilled: number;
  trainerSlotsTotal: number;
  enrollmentCount: number;
}

export interface AttendanceSummary {
  present: number;
  late: number;
  excused: number;
  absent: number;
  attended: number;
  required: number;
  totalSessions: number;
  meetsRequirement: boolean;
}

export interface TrainingEnrollment {
  id: number;
  status: 'enrolled' | 'withdrawn' | 'completed';
  completedAt: string | null;
  planter: {
    id: number;
    name: string;
    spouseName: string | null;
    email: string | null;
    hasLogin: boolean;
    plantingStep: string | null;
    trainingCompletedAt: string | null;
  } | null;
  attendance: { sessionId: number; status: AttendanceStatus; note: string | null }[];
  summary: AttendanceSummary;
}

export interface SessionDetail extends TrainingSession {
  semester: TrainingSemester;
  permissions: {
    canEdit: boolean;
    canChangeVenue: boolean;
    canTakeAttendance: boolean;
    canReleaseOwnSlot: boolean;
    releaseCutoffDays: number;
  };
}

export interface AttendanceRow {
  enrollmentId: number;
  planter: { id: number; name: string; spouseName: string | null };
  status: AttendanceStatus | null;
  note: string | null;
}

export interface SessionInput {
  module?: string;
  part?: number | null;
  title?: string;
  description?: string | null;
  date?: string;
  startTime?: string;
  durationMinutes?: number | null;
  venueId?: number | null;
  notes?: string | null;
  status?: 'scheduled' | 'cancelled';
  outcomes?: TrainingOutcome[];
}

const data = <T>(p: Promise<{ data: { data: T } }>) => p.then((r) => r.data.data);

export const trainingService = {
  semesters: () => data<TrainingSemesterSummary[]>(apiClient.get('/training/semesters')),
  semester: (id: number) => data<TrainingSemester & { sessions: TrainingSession[] }>(apiClient.get(`/training/semesters/${id}`)),
  createSemester: (body: Partial<TrainingSemester>) => data<TrainingSemester>(apiClient.post('/training/semesters', body)),
  updateSemester: (id: number, body: Partial<TrainingSemester>) => data<TrainingSemester>(apiClient.patch(`/training/semesters/${id}`, body)),
  deleteSemester: (id: number) => apiClient.delete(`/training/semesters/${id}`),

  createSession: (semesterId: number, body: SessionInput) => data<TrainingSession>(apiClient.post(`/training/semesters/${semesterId}/sessions`, body)),
  session: (id: number) => data<SessionDetail>(apiClient.get(`/training/sessions/${id}`)),
  updateSession: (id: number, body: SessionInput) => data<TrainingSession>(apiClient.patch(`/training/sessions/${id}`, body)),
  deleteSession: (id: number) => apiClient.delete(`/training/sessions/${id}`),
  remind: (id: number, audience: 'all' | 'trainers' | 'planters') =>
    data<{ sent: number; failed: number; reasons: string[] }>(apiClient.post(`/training/sessions/${id}/remind`, { audience })),

  trainers: () => data<{ id: number; firstName: string; lastName: string; email: string }[]>(apiClient.get('/training/trainers')),
  assignSlot: (sessionId: number, slot: TrainerSlotName, userId: number | null) =>
    data<TrainingSession>(apiClient.put(`/training/sessions/${sessionId}/trainers/${slot}`, { userId })),
  claimSlot: (sessionId: number, slot: TrainerSlotName) => data<TrainingSession>(apiClient.post(`/training/sessions/${sessionId}/claim`, { slot })),
  releaseSlot: (sessionId: number) => data<TrainingSession>(apiClient.post(`/training/sessions/${sessionId}/release`)),

  venues: () => data<TrainingVenue[]>(apiClient.get('/training/venues')),
  createVenue: (body: Partial<TrainingVenue>) => data<TrainingVenue>(apiClient.post('/training/venues', body)),
  updateVenue: (id: number, body: Partial<TrainingVenue>) => data<TrainingVenue>(apiClient.patch(`/training/venues/${id}`, body)),

  enrollments: (semesterId: number) => data<TrainingEnrollment[]>(apiClient.get(`/training/semesters/${semesterId}/enrollments`)),
  eligiblePlanters: (semesterId: number, search?: string) =>
    data<{ id: number; name: string; spouseName: string | null; email: string | null; hasLogin: boolean; city: string | null; state: string | null }[]>(
      apiClient.get(`/training/semesters/${semesterId}/eligible-planters`, { params: { search } }),
    ),
  enroll: (semesterId: number, planterIds: number[]) => data<{ enrolled: number }>(apiClient.post(`/training/semesters/${semesterId}/enrollments`, { planterIds })),
  setEnrollmentStatus: (id: number, status: 'enrolled' | 'withdrawn') => apiClient.patch(`/training/enrollments/${id}`, { status }),
  completeEnrollment: (id: number, body: { override?: boolean; advancePipeline?: boolean }) =>
    data<{ pipeline: { advanced: boolean; reason?: string } }>(apiClient.post(`/training/enrollments/${id}/complete`, body)),

  attendance: (sessionId: number) => data<AttendanceRow[]>(apiClient.get(`/training/sessions/${sessionId}/attendance`)),
  saveAttendance: (sessionId: number, records: { enrollmentId: number; status: AttendanceStatus | null; note?: string | null }[]) =>
    apiClient.put(`/training/sessions/${sessionId}/attendance`, { records }),
};

export const apiError = (error: unknown, fallback = 'Something went wrong') =>
  (error as any)?.response?.data?.message || fallback;

export const formatSessionDate = (iso: string, timeZone: string) =>
  new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(iso));

export const formatSessionWindow = (start: string, end: string, timeZone: string) => {
  const t = (iso: string) => new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', minute: '2-digit' }).format(new Date(iso));
  return `${t(start)}–${t(end)}`;
};

export const ATTENDANCE_LABELS: Record<AttendanceStatus, string> = {
  present: 'Present',
  late: 'Late',
  excused: 'Excused',
  absent: 'Absent',
};
